/**
 * Caruselul de clipuri reale.
 *
 * Trei treburi:
 *   1. Clipurile rulează doar cât sunt pe ecran.
 *   2. Săgețile mută pista cu exact un card.
 *   3. Un singur clip poate avea sunet, niciodată două.
 *
 * Punctul unu nu e o optimizare, e condiția de a putea pune opt clipuri într-o
 * pagină de trafic plătit. Opt bucle mute care decodează în același timp se bat
 * cu hero-ul pe lățime de bandă și pe decodor, pe un telefon care deja plătește
 * pentru 117 cadre de hero. Cu `preload="none"` în markup, un clip care n-a
 * intrat niciodată pe ecran nu costă niciun octet.
 */

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/* ------------------------------------------------------------- clipuri --- */

const modeOf = (video) => video.closest('[data-puffy-proof-mode]')?.dataset.puffyProofMode || 'auto';

function initClips(root) {
  // Clipurile pe „atingere" nu pornesc singure, deci nu intră în observator.
  // Ele se opresc totuși când ies de pe ecran, în initTap.
  const videos = [...root.querySelectorAll('.puffy-proof__video')].filter((v) => modeOf(v) !== 'tap');
  if (!videos.length) return;

  // Buclele care pornesc singure sunt mișcare. Cine a cerut mai puțină mișcare
  // primește posterul și un control, nu un clip care începe de la sine.
  if (matchMedia(REDUCED_MOTION).matches) {
    for (const video of videos) {
      video.removeAttribute('autoplay');
      video.setAttribute('controls', '');
      video.pause();
    }
    return;
  }

  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = entry.target;
        if (entry.isIntersecting) {
          if (video.preload === 'none') video.preload = 'auto';
          video.play().catch(() => {
            /* Redare refuzată, mod economic. Posterul ține locul. */
          });
        } else {
          video.pause();
        }
      }
    },
    // Pe orizontală pragul contează la fel de mult ca pe verticală: un card
    // tăiat pe jumătate în dreapta e tot pe ecran.
    { rootMargin: '100px 100px' }
  );

  for (const video of videos) observer.observe(video);
}

/* --------------------------------------------------------------- sunet --- */

function initSound(root) {
  const buttons = [...root.querySelectorAll('[data-puffy-proof-sound]')];
  if (!buttons.length) return;

  const videoOf = (btn) => btn.closest('.puffy-proof__media')?.querySelector('video');

  for (const btn of buttons) {
    btn.addEventListener('click', () => {
      const video = videoOf(btn);
      if (!video) return;
      const turningOn = video.muted;

      // Un singur clip cu sunet. Două voci în același timp e o pagină pe care o
      // închizi, nu una din care cumperi. Se sting și clipurile pornite manual,
      // nu doar cele cu buton de sunet.
      for (const v of root.querySelectorAll('.puffy-proof__video')) {
        if (v === video) continue;
        v.muted = true;
        if (modeOf(v) === 'tap') v.pause();
      }
      for (const other of buttons) {
        if (other === btn) continue;
        other.setAttribute('aria-pressed', 'false');
        other.setAttribute('aria-label', 'Pornește sunetul');
      }
      for (const play of root.querySelectorAll('[data-puffy-proof-play]')) {
        play.setAttribute('aria-pressed', 'false');
        play.setAttribute('aria-label', 'Pornește clipul');
      }

      video.muted = !turningOn;
      btn.setAttribute('aria-pressed', String(turningOn));
      btn.setAttribute('aria-label', turningOn ? 'Oprește sunetul' : 'Pornește sunetul');

      // Cu sunet pornit clipul trebuie să și meargă, altfel butonul pare rupt.
      if (turningOn) video.play().catch(() => {});
    });
  }
}

/* ------------------------------------------------------------ atingere --- */

/**
 * Clipurile care pornesc doar la atingere.
 *
 * Pornesc cu sunet, pentru că atingerea e exact consimțământul de care are
 * nevoie browserul ca să lase sunetul. Un clip pe care l-ai apăsat și care
 * pornește mut e o promisiune ratată.
 *
 * Dacă sunetul tot e refuzat, clipul pornește mut în loc să nu pornească deloc:
 * mai bine imagine fără sunet decât un buton care pare stricat.
 */
function initTap(root) {
  const buttons = [...root.querySelectorAll('[data-puffy-proof-play]')];
  if (!buttons.length) return;

  const videoOf = (btn) => btn.closest('.puffy-proof__media')?.querySelector('video');

  // Cât rulează un clip, pista stă pe loc. Altfel cardul pe care tocmai ai
  // apăsat pleacă încet de sub deget, ceea ce e mai rău decât lipsa mișcării.
  const track = root.querySelector('[data-puffy-proof-track]');
  let tinut = false;
  const tine = () => {
    if (tinut) return;
    tinut = true;
    track?.puffyAutoscroll?.hold();
  };
  const lasa = () => {
    if (!tinut) return;
    if ([...root.querySelectorAll('.puffy-proof__video')].some((v) => !v.paused && !v.muted)) return;
    tinut = false;
    track?.puffyAutoscroll?.release();
  };

  const stopAll = (except) => {
    for (const other of buttons) {
      const v = videoOf(other);
      if (!v || v === except) continue;
      v.pause();
      v.muted = true;
      other.setAttribute('aria-pressed', 'false');
      other.setAttribute('aria-label', 'Pornește clipul');
    }
    // Și clipurile care rulează singure își pierd sunetul: o singură voce.
    for (const v of root.querySelectorAll('.puffy-proof__video')) {
      if (v !== except) v.muted = true;
    }
    for (const s of root.querySelectorAll('[data-puffy-proof-sound]')) {
      s.setAttribute('aria-pressed', 'false');
      s.setAttribute('aria-label', 'Pornește sunetul');
    }
  };

  for (const btn of buttons) {
    btn.addEventListener('click', () => {
      const video = videoOf(btn);
      if (!video) return;

      if (!video.paused) {
        video.pause();
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', 'Pornește clipul');
        lasa();
        return;
      }

      stopAll(video);
      tine();
      if (video.preload === 'none') video.preload = 'auto';
      video.muted = false;
      btn.setAttribute('aria-pressed', 'true');
      btn.setAttribute('aria-label', 'Oprește clipul');

      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => {
          btn.setAttribute('aria-pressed', 'false');
          btn.setAttribute('aria-label', 'Pornește clipul');
          lasa();
        });
      });
    });
  }

  // Un clip pornit manual nu trebuie să continue să ruleze după ce a ieșit din
  // ecran, altfel se aude o voce dintr-o secțiune pe care nu o mai vezi.
  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) continue;
        const btn = entry.target.querySelector('[data-puffy-proof-play]');
        const video = entry.target.querySelector('video');
        if (!video || video.paused) continue;
        video.pause();
        video.muted = true;
        btn?.setAttribute('aria-pressed', 'false');
        btn?.setAttribute('aria-label', 'Pornește clipul');
        lasa();
      }
    },
    { rootMargin: '0px' }
  );

  for (const btn of buttons) {
    const media = btn.closest('.puffy-proof__media');
    if (media) observer.observe(media);
  }
}

/* -------------------------------------------------------------- săgeți --- */

function initArrows(root) {
  const track = root.querySelector('[data-puffy-proof-track]');
  const prev = root.querySelector('[data-puffy-proof-prev]');
  const next = root.querySelector('[data-puffy-proof-next]');
  if (!track || !prev || !next) return;

  // Cu banda continuă pista nu mai e un scroller, deci săgețile n-ar avea ce
  // muta. Un buton care nu face nimic e mai rău decât lipsa lui.
  if (track.closest('.puffy-belt')) {
    prev.closest('.puffy-proof__nav')?.remove();
    return;
  }

  // Un card plus spațiul dintre carduri, citit din pagină, nu presupus: lățimea
  // se schimbă la fiecare prag de ecran și o valoare fixă ar sări peste carduri
  // pe unele lățimi.
  const step = () => {
    const first = track.firstElementChild;
    const second = first?.nextElementSibling;
    if (!first) return 0;
    if (second) return second.getBoundingClientRect().left - first.getBoundingClientRect().left;
    return first.getBoundingClientRect().width;
  };

  const sync = () => {
    const max = track.scrollWidth - track.clientWidth;
    prev.disabled = track.scrollLeft <= 1;
    next.disabled = track.scrollLeft >= max - 1;
  };

  /**
   * Mută pista cu un card, și verifică după aceea că s-a mutat.
   *
   * Derularea lină pornită din JS nu se execută în toate condițiile: cu
   * `scroll-behavior: smooth` moștenit de la temă, redarea încetinită sau modul
   * economic, `scrollBy` cu `smooth` se poate întoarce fără să miște nimic.
   * Deja a costat două componente în proiectul ăsta. Aici, dacă după 250ms
   * poziția e neschimbată, saltul se face direct: o săgeată care uneori nu face
   * nimic e mai rea decât una fără animație.
   */
  const move = (dir) => {
    const from = track.scrollLeft;
    const by = dir * step();
    track.scrollBy({ left: by, behavior: 'smooth' });
    setTimeout(() => {
      if (Math.abs(track.scrollLeft - from) < 2) {
        track.scrollTo({ left: from + by, behavior: 'instant' });
      }
      sync();
    }, 250);

    // Starea săgeților se recitește și după ce se așază derularea lină, nu doar
    // din evenimentul `scroll`: acela nu e garantat după o derulare pornită din
    // cod, iar o săgeată rămasă gri când mai e unde merge arată ca un capăt de
    // listă care nu există.
    setTimeout(sync, 700);
  };

  prev.addEventListener('click', () => move(-1));
  next.addEventListener('click', () => move(1));

  track.addEventListener('scroll', sync, { passive: true });
  addEventListener('resize', sync, { passive: true });
  sync();
}

/* --------------------------------------------------------------- start --- */

function init() {
  for (const root of document.querySelectorAll('[data-puffy-proof]')) {
    initClips(root);
    initSound(root);
    initTap(root);
    initArrows(root);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
